import { useEffect, useRef, useState, useSyncExternalStore } from "react";
import type { UseFormReturn, FieldPath, FieldValues } from "react-hook-form";

// Mirrors `source` field into `target` field as long as the user has not
// manually edited the target. Editing the target anchors it; clearing it
// (empty string) un-anchors it and mirror resumes.
//
// Implementation note: anchored state lives in a ref so we can update it from
// within RHF's `watch` subscription without triggering re-renders during the
// effect body (avoids set-state-in-effect lint). We expose the ref to render
// via useSyncExternalStore.
export function useStickyMirror<TValues extends FieldValues>(args: {
  form: UseFormReturn<TValues>;
  source: FieldPath<TValues>;
  target: FieldPath<TValues>;
  /**
   * Optional transform of the source value before writing to target. Used by Org.name to append "
   * Organization" to User.name.
   */
  derive?: (sourceValue: string) => string;
}): { isMirrored: boolean } {
  const { form, source, target, derive } = args;

  // External store for anchored state — readable in render without warnings.
  const anchoredRef = useRef(false);
  const listenersRef = useRef(new Set<() => void>());
  const [subscribe] = useState(() => (cb: () => void) => {
    listenersRef.current.add(cb);
    return () => listenersRef.current.delete(cb);
  });
  const getSnapshot = () => anchoredRef.current;
  const anchored = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

  const setAnchored = (next: boolean) => {
    if (anchoredRef.current === next) return;
    anchoredRef.current = next;
    listenersRef.current.forEach((cb) => cb());
  };

  // Subscribe to RHF value changes — react to target/source via the form's
  // own watch subscription, not via render-time `watch()`.
  useEffect(() => {
    const subscription = form.watch((values, info) => {
      const targetValue = (getByPath(values, target) ?? "") as string;
      const sourceValue = (getByPath(values, source) ?? "") as string;
      const derived = derive ? derive(sourceValue) : sourceValue;

      // User just cleared the target → un-anchor.
      if (info.name === target && targetValue === "") {
        setAnchored(false);
      }

      // User edited the target manually → anchor.
      if (info.name === target && info.type === "change" && targetValue !== "" && targetValue !== derived) {
        setAnchored(true);
      }

      // Source changed and not anchored → push derived into target.
      if (!anchoredRef.current && info.name === source && targetValue !== derived) {
        form.setValue(target, derived as never, {
          shouldDirty: false,
          shouldTouch: false,
          shouldValidate: false,
        });
      }
    });
    return () => subscription.unsubscribe();
  }, [form, source, target, derive]);

  // Initial mirror on mount (source already has value before this hook runs).
  useEffect(() => {
    const sourceValue = (form.getValues(source) ?? "") as string;
    const targetValue = (form.getValues(target) ?? "") as string;
    const derived = derive ? derive(sourceValue) : sourceValue;
    if (!anchoredRef.current && targetValue !== derived) {
      form.setValue(target, derived as never, {
        shouldDirty: false,
        shouldTouch: false,
        shouldValidate: false,
      });
    }
    // Run once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { isMirrored: !anchored };
}

function getByPath(obj: unknown, path: string): unknown {
  return path
    .split(".")
    .reduce<unknown>(
      (acc, key) => (acc && typeof acc === "object" ? (acc as Record<string, unknown>)[key] : undefined),
      obj,
    );
}
